import {Component, DestroyRef, inject, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {
    MobileOperatorEventSource,
    MsisdnFraudEntryApiService,
    MsisdnFraudEntryRequest,
} from '@npck/af-frontend-api';
import {getFormattedDateOrNull, LocalizationService, RouterService, rxSpinner} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {finalize, Observable, of} from 'rxjs';
import {DateForm} from '../../../../../components/date-picker-field/date-picker-field.component';
import {formatMsisdnDisplay} from '../../../../../domain/phone-utils';
import {INCIDENT_ID, MSISDN_FRAUD_ENTRY_ID} from '../../../../../domain/query-params.keys';
import {MainContainerComponent} from '../../../../../layout/main-container/main-container.component';
import {Path} from '../../../../../path';
import {
    buildMsisdnFraudEntryControls,
    MsisdnFraudEntryForm,
    SubscriberMsisdnForm,
} from '../../../create-edit/msisdn-fraud-step/msisdn-fraud-form/msisdn-fraud-form.component';
import {createMsisdnFraudEntryRequest} from '../../../create-edit/msisdn-fraud-step/msisdn-fraud-step.component';

const TIME_UI_FORMAT = 'HH:mm';

@Component({
    selector: 'af-msisdn-fraud-entry-create-edit',
    standalone: false,
    templateUrl: './msisdn-fraud-entry-create-edit.component.html',
})
export class MsisdnFraudEntryCreateEditComponent {
    @ViewChild('mainContainer') mainContainer: MainContainerComponent;
    Path = Path;
    form: FormGroup<MsisdnFraudEntryForm>;
    incidentId: string;
    entryId: string;
    isFormReady = false;
    lockedControls: AbstractControl[] = [];
    private destroyRef = inject(DestroyRef);

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private msisdnFraudEntryApiService: MsisdnFraudEntryApiService,
        private localizationService: LocalizationService,
        private dialogService: DialogService,
        private routerService: RouterService,
    ) {
        this.incidentId = this.activatedRoute.snapshot.params[INCIDENT_ID];
        this.entryId = this.activatedRoute.snapshot.params[MSISDN_FRAUD_ENTRY_ID];
        this.form = this.buildForm();

        if (this.entryId) {
            this.msisdnFraudEntryApiService.getMsisdnFraudEntry(this.incidentId, this.entryId)
                .pipe(rxSpinner('initial'))
                .subscribe(response => {
                    this.patchForm(response);
                    this.lockFilledControls(response);
                    this.isFormReady = true;
                });
        } else {
            this.isFormReady = true;
        }
    }

    filter = (path: string): boolean => {
        return path === Path.MSISDN_FRAUD_ENTRIES;
    };

    breadcrumbCustomizer = (path: string): string => {
        if (this.incidentId === path) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#IncidentId', {[INCIDENT_ID]: this.incidentId});
        }
        if (path === Path.MSISDN_FRAUD_ENTRIES) {
            return '...';
        }
        return path;
    };

    customNavigateToPath = (path?: string): boolean | void => {
        if (path === Path.MSISDN_FRAUD_ENTRIES) {
            this.routerService.navigate(`${Path.INCIDENTS}/${this.incidentId}`, {queryParamsHandling: 'merge'});
            return;
        }
        return true;
    };

    navigateBack = (): Observable<boolean> => {
        if (this.form.dirty) {
            return this.dialogService.openDialog({
                title: 'i18n_af_dialogCreateIncidentWarningBeforeExitTitle',
                subtitle: 'i18n_af_dialogCreateIncidentWarningBeforeExitSubtitle',
                buttonOkText: 'i18n_af_buttonExit',
                buttonCancelText: 'i18n_af_buttonCancel',
                isButtonOkAccent: true,
            }, {disableClose: true});
        }
        return of(true);
    };

    save(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        if (this.entryId && this.form.pristine) {
            return;
        }
        const request = createMsisdnFraudEntryRequest(this.form);
        const saveRequest = this.entryId
            ? this.msisdnFraudEntryApiService.updateMsisdnFraudEntry(this.incidentId, this.entryId, request)
            : this.msisdnFraudEntryApiService.createMsisdnFraudEntry(this.incidentId, request);
        this.form.disable({emitEvent: false});
        saveRequest
            .pipe(
                rxSpinner('saveEdit'),
                finalize(() => {
                    this.form.enable({emitEvent: false});
                    this.applyLocks();
                }),
            )
            .subscribe(() => this.mainContainer.breadcrumbs.navigateBackSkipListener());
    }

    private buildForm(): FormGroup<MsisdnFraudEntryForm> {
        const form = this.formBuilder.group<MsisdnFraudEntryForm>({
            msisdn: new FormControl(null, [Validators.required, Validators.maxLength(15)]),
            selectedPhoneInfo: new FormControl(null),
            operator: new FormControl(null, [Validators.required]),
            activationDate: this.formBuilder.group<DateForm>({
                date: new FormControl(null),
            }),
            terminationDate: this.formBuilder.group<DateForm>({
                date: new FormControl(null),
            }),
            status: new FormControl(null, [Validators.required]),
            reason: new FormControl(MobileOperatorEventSource.OPERATOR_FRAUD_SYSTEM),
        });
        buildMsisdnFraudEntryControls(form, this.formBuilder, this.destroyRef);
        return form;
    }

    private patchForm(entry: MsisdnFraudEntryRequest): void {
        const activationDate = entry.activationDate ? new Date(entry.activationDate) : null;
        const terminationDate = entry.terminationDate ? new Date(entry.terminationDate) : null;
        const contactDate = entry.contactDate ? new Date(entry.contactDate) : null;
        this.form.patchValue({
            msisdn: formatMsisdnDisplay(entry.msisdn),
            operator: entry.operator,
            status: entry.status,
            reason: entry.reason,
            category: entry.category,
            isResident: entry.isResident,
            iin: entry.iin,
            passportNumber: entry.passportNumber,
            lastName: entry.lastName,
            firstName: entry.firstName,
            middleName: entry.middleName,
            country: entry.country,
            activationDate: {date: activationDate},
            terminationDate: {date: terminationDate},
            contactDate: {date: contactDate, time: getFormattedDateOrNull(contactDate, TIME_UI_FORMAT)},
        });
        entry.subscriberMsisdns?.forEach(msisdn => this.form.controls.subscriberMsisdns.push(
            new FormGroup<SubscriberMsisdnForm>({
                msisdn: new FormControl(formatMsisdnDisplay(msisdn)),
                selectedPhoneInfo: new FormControl(null),
            }),
        ));
    }

    private lockFilledControls(entry: MsisdnFraudEntryRequest): void {
        const controls = this.form.controls;
        const storedValues: [AbstractControl, unknown][] = [
            [controls.msisdn, entry.msisdn],
            [controls.selectedPhoneInfo, entry.msisdn],
            [controls.operator, entry.operator],
            [controls.status, entry.status],
            [controls.reason, entry.reason],
            [controls.category, entry.category],
            [controls.isResident, entry.isResident],
            [controls.iin, entry.iin],
            [controls.passportNumber, entry.passportNumber],
            [controls.lastName, entry.lastName],
            [controls.firstName, entry.firstName],
            [controls.middleName, entry.middleName],
            [controls.country, entry.country],
            [controls.activationDate, entry.activationDate],
            [controls.terminationDate, entry.terminationDate],
            [controls.contactDate, entry.contactDate],
        ];
        this.lockedControls = [
            ...storedValues.filter(([, value]) => value !== null && value !== undefined && value !== '').map(([control]) => control),
            ...controls.subscriberMsisdns.controls,
        ];
        this.applyLocks();
    }

    private applyLocks(): void {
        this.lockedControls.forEach(control => control.disable({emitEvent: false}));
    }
}
